/**
 * graphene_tool_utils.ts
 * Utility to deactivate graphene multicut / merge tools in the viewer.
 * Shared between SplitMergeOverlay.vue and main.ts (Escape handler).
 */

/**
 * Deactivate the active graphene multicut or merge tool.
 *
 * Uses the neuroglancer SelectedLegacyTool API: setting layer.tool.value
 * to undefined properly calls dispose() on the tool and cleans up all
 * event listeners and in-progress annotations.
 *
 * We run all known approaches in sequence — even if approach 1 succeeds, we
 * also call toolBinder.deactivate_() to clear any straggler activation. Some
 * NG builds keep the legacy tool reference alive while the toolBinder still
 * holds an activation; only clearing both fully exits.
 */
/** What kind of tool this is. The live site is minified, so class names are
 *  single letters there and never contain "multicut" or "merge" (the Cancel
 *  button did nothing, Ames 2026-10-03); the tool's own saved type survives. */
function toolKind(tool: any): string {
  let json = '';
  try { const j = tool?.toJSON?.(); json = typeof j === 'string' ? j : JSON.stringify(j ?? ''); } catch { /* no saved form */ }
  return `${tool?.constructor?.name || ''} ${json}`.toLowerCase();
}
const isGrapheneTool = (kind: string) => /multicut|merge|graphene|line/.test(kind);

export function exitGrapheneTool() {
  const viewer = (window as any)['viewer'];
  if (!viewer) return;
  let didSomething = false;

  // Approach 1: Set the selected layer's tool.value to undefined.
  // SelectedLegacyTool.set(undefined) → unregister() → tool.dispose()
  try {
    const layer = viewer.selectedLayer?.layer?.layer;
    if (layer && layer.tool && layer.tool.value !== undefined) {
      const toolName = toolKind(layer.tool.value);
      if (isGrapheneTool(toolName)) {
        console.info('[graphene_tool_utils] Disposing tool via selectedLayer.tool.value =', toolName);
        layer.tool.value = undefined;
        didSomething = true;
      }
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Approach 1 (selectedLayer.tool.value) failed:', e);
  }

  // Approach 2: Walk all managed layers — even if approach 1 ran, the active
  // tool may live on a different layer than the selected one.
  try {
    for (const ml of viewer.layerManager?.managedLayers ?? []) {
      const userLayer = ml.layer;
      if (!userLayer || !userLayer.tool) continue;
      const tool = userLayer.tool.value;
      if (!tool) continue;
      const name = toolKind(tool);
      if (isGrapheneTool(name)) {
        console.info('[graphene_tool_utils] Disposing tool on layer', ml.name, ':', name);
        userLayer.tool.value = undefined;
        didSomething = true;
      }
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Approach 2 (iterate layers) failed:', e);
  }

  // Approach 3: Use the globalToolBinder to clear any tool activation. This
  // covers the case where layer.tool.value is undefined but a toolBinder
  // activation is still keeping the overlay open.
  try {
    // The GLOBAL binder holds the activation. viewer.toolBinder is the local
    // one, which has neither activeTool_ nor deactivate_, and it used to be
    // picked first, so this step never did anything.
    const gtb = viewer.globalToolBinder || viewer.toolBinder?.globalBinder || viewer.toolBinder;
    if (gtb?.activeTool_) {
      console.info('[graphene_tool_utils] Cancelling activeTool_ via globalToolBinder');
      try { gtb.activeTool_.cancel?.(); } catch {}
      didSomething = true;
    }
    if (typeof gtb?.deactivate_ === 'function') {
      console.info('[graphene_tool_utils] Calling globalToolBinder.deactivate_()');
      gtb.deactivate_();
      didSomething = true;
    }
  } catch (e) {
    console.warn('[graphene_tool_utils] Approach 3 (globalToolBinder) failed:', e);
  }

  // Approach 4: If the DOM overlay is still present after all the above, hide
  // the selectedLayer panel as a last resort to remove the visible UI.
  try {
    const stillThere = !!(
      document.querySelector('.graphene-multicut') ||
      document.querySelector('.graphene-merge-segments')
    );
    if (stillThere) {
      if (!didSomething) {
        console.info('[graphene_tool_utils] Fallback — DOM overlay still present, hiding selectedLayer panel');
      }
      viewer.selectedLayer.visible = false;
    }
  } catch {}
}

/** Switch on cut (multicut), merge or Find Path on the segmentation layer,
 *  as the toolbar buttons do (moved here from ExtensionBar.vue). */
export function activateGrapheneTool(toolType: 'multicut' | 'merge' | 'findPath') {
  const viewer: any = (window as any)['viewer'];
  if (!viewer) return;

  // 1. Select the segmentation layer (required for tool keybindings)
  try {
    const segLayer = viewer.layerManager?.managedLayers?.find(
      (x: any) => String(x.layer?.type || '').startsWith('segmentation'),   // by the layer's own type: class names are renamed in the production build
    );
    if (segLayer) {
      viewer.selectedLayer.layer = segLayer;
      viewer.selectedLayer.visible = true;
    }
  } catch { /* non-critical */ }

  // 2. Dispatch keyboard shortcut to the viewer element where ng binds handlers.
  // CUSTOM_BINDINGS map (in dev-server / build-prod): keyc → grapheneMulticutSegments,
  // keym → grapheneMergeSegments, keyf → grapheneFindPath.
  const keyMap = { multicut: 'c', merge: 'm', findPath: 'f' } as const;
  const codeMap = { multicut: 'KeyC', merge: 'KeyM', findPath: 'KeyF' } as const;
  const key = keyMap[toolType];
  const eventInit: KeyboardEventInit = {
    key, code: codeMap[toolType],
    bubbles: true, cancelable: true,
  };
  // Try viewer.element first (where global inputEventBindings live), then fallback
  const targets = [
    viewer.element,
    viewer.display?.container,
    document.getElementById('neuroglancer-container'),
  ].filter(Boolean);
  // preventScroll: on a touch screen the page is taller than what shows (the
  // browser bar), and a plain focus() slid it up, so the top bar went off
  // screen for good (Nseraf, report 135, on a Galaxy Tab).
  for (const el of targets) {
    if (el instanceof HTMLElement) el.focus({ preventScroll: true });
    el.dispatchEvent(new KeyboardEvent('keydown', eventInit));
  }
}
